import { useState, useEffect, useRef } from 'react';

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = formRef.current;
    if (!form) return;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch('/contact.php', {
  method: 'POST',
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  body: params.toString(),
});

if (!response.ok) {
  throw new Error('Error al enviar el formulario');
}

      setSubmitted(true);
    } catch {
      setError('Ha ocurrido un error. Inténtelo de nuevo por favor.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  return (
    <section id="contact" ref={ref} className="relative pt-12 md:pt-16 pb-20 md:pb-28 bg-primary-500">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Título y subtítulo */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 leading-tight font-medium mb-4">
            ¿Necesitas <span className="italic text-accent-400">asesoramiento</span> legal?
          </h2>
          <p className="text-sm md:text-base text-foreground-300 max-w-xl mx-auto leading-relaxed md:whitespace-nowrap">Completa el formulario y nos pondremos en contacto en menos de 24 horas.</p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 transition-all duration-800 ease-out delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Formulario */}
          <div className="bg-primary-600 rounded-2xl p-8 md:p-10 border border-primary-400/20">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-2xl text-foreground-950"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-50 mb-2">Mensaje recibido</h3>
                <p className="text-sm text-foreground-300 leading-relaxed">
                  Gracias por contactar con GOU Abogados. Un miembro de nuestro equipo se pondrá en contacto con usted en las próximas 24 horas.
                </p>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                </div>

                <h3 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">
                  Envía tu consulta
                </h3>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-200 mb-2">
                    Nombre completo <span className="text-accent-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-primary-700/50 border border-primary-400/20 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all duration-300"
                    placeholder="Tu nombre"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-200 mb-2">
                    Email <span className="text-accent-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-primary-700/50 border border-primary-400/20 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all duration-300"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-200 mb-2">
                    Teléfono <span className="text-accent-400">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-primary-700/50 border border-primary-400/20 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all duration-300"
                    placeholder="+54 351 123 4567"
                  />
                </div>

                <div>
                  <label htmlFor="area" className="block text-sm font-medium text-foreground-200 mb-2">
                    Asunto <span className="text-accent-400">*</span>
                  </label>
                  <select
                    id="area"
                    name="area"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-primary-700/50 border border-primary-400/20 text-sm text-foreground-50 outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all duration-300 appearance-none cursor-pointer"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" }}
                  >
                    <option value="">Selecciona un área</option>
                    <option value="danos-perjuicios">Daños y perjuicios</option>
                    <option value="familia">Familia</option>
                    <option value="laboral">Laboral</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-200 mb-2">
                    Mensaje <span className="text-accent-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    onChange={handleMessageChange}
                    className="w-full px-4 py-3 rounded-lg bg-primary-700/50 border border-primary-400/20 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all duration-300 resize-none"
                    placeholder="Describe brevemente tu consulta (máximo 500 caracteres)"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1.5">{charCount}/500 caracteres</p>
                </div>

                {error && (
                  <p className="text-sm text-red-400 bg-red-950/30 px-4 py-2.5 rounded-lg border border-red-800/50">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-3.5 rounded-full bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-400 hover:scale-[1.02] active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </span>
                  ) : (
                    'Enviar Consulta'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Mapa + Contacto Directo */}
          <div className="flex flex-col gap-6">
            <a
              href="https://maps.app.goo.gl/3uAVjdb6ApNEELNk9"
              target="_blank"
              rel="noopener noreferrer"
              className="relative rounded-2xl overflow-hidden border border-primary-400/20 bg-primary-600 min-h-[240px] md:min-h-[300px] block group cursor-pointer"
            >
              <img
                src="https://static.readdy.ai/image/f9226af7b0c833b6273ae5c168cd92f2/62caf25730bb3d5a9e07da8f47ee266d.png"
                alt="Ubicación de GOU Abogados - Accidentes de Tránsito en Córdoba"
                className="w-full h-full object-cover"
                style={{ minHeight: '240px' }}
              />
              {/* Overlay verde sutil */}
              <div className="absolute inset-0 bg-primary-500/20 group-hover:bg-primary-500/10 transition-colors duration-300 pointer-events-none"></div>
              {/* Badge hover "Ver en Maps" */}
              <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-50/90 text-primary-700 text-xs font-semibold shadow-sm">
                  <i className="ri-map-pin-line"></i>
                  Ver en Google Maps
                </span>
              </div>
            </a>

            <div className="bg-accent-500 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-foreground-950/10 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xl text-foreground-950"></i>
                </div>
                <h4 className="font-heading text-lg font-bold text-foreground-950">Contacto Directo</h4>
              </div>
              <p className="text-sm text-foreground-800 leading-relaxed mb-6">
                Para consultas legales, podés comunicarte directamente por WhatsApp. La atención es personalizada y confidencial.
              </p>
              <a
                href="https://wa.me/543513495570?text=Hola.%20Quisiera%20hablar%20con%20un%20abogado%20por%20un%20asunto%20legal."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-foreground-50 text-sm font-semibold hover:bg-foreground-900 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom - transition to Footer (background-200) */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden"
        style={{ height: '60px', marginBottom: '-1px' }}
      >
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block"
          style={{ display: 'block' }}
        >
          <path d="M0 60H1440V30C1080 12 360 48 0 30V60Z" fill="oklch(0.86 0.016 95)" stroke="none" />
        </svg>
      </div>
    </section>
  );
}